import { Phone, Mail, Clock, FileCheck, Handshake, ShieldAlert } from "lucide-react";
import Eyebrow from "../components/Eyebrow";
import Breadcrumbs from "../components/Breadcrumbs";
import ProfessionalReferralForm from "../components/forms/ProfessionalReferralForm";
import { Reveal, KineticLines } from "../components/Reveal";
import { usePageMeta } from "../lib/seo";
import { SITE, PRO_CHIPS, CARE_OPTIONS } from "../lib/site";

const PROCESS = [
  {
    icon: FileCheck,
    title: "Send what you know",
    body: "Share the family's contact details, a general care need, a target area, and the timing, through the referral form below, by phone, or by email. No sensitive medical information, please.",
  },
  {
    icon: Clock,
    title: "Doris responds within 24 hours",
    body: "CareCraft responds to calls and emails within 24 hours.",
  },
  {
    icon: Handshake,
    title: "CareCraft works directly with the family",
    body: "Doris takes it from there: understanding the person, narrowing the possibilities, coordinating visits, and staying alongside the family through the decision and transition.",
  },
];

export default function ForProfessionals() {
  usePageMeta({
    title: "For Professionals: Refer a Family",
    description:
      "A care placement resource for hospital social workers, discharge planners, case managers, nurses, physicians, and hospice professionals in Los Angeles. Referrals answered within 24 hours.",
    path: "/for-professionals",
  });

  return (
    <>
      <section className="bg-ivory pt-[72px]">
        <div className="mx-auto max-w-site px-4 pb-16 pt-10 sm:px-6 md:pb-20 md:pt-14 lg:px-12">
          <Breadcrumbs trail={[{ label: "For Professionals" }]} />
          <div className="mt-10 max-w-3xl">
            <Eyebrow>For healthcare professionals</Eyebrow>
            <h1 className="mt-6 font-serif text-4xl font-medium leading-[1.15] tracking-tight text-forest sm:text-5xl lg:text-[3.4rem]">
              <KineticLines
                lines={[<>Working together to support</>, <>families well.</>]}
              />
            </h1>
            <Reveal delay={0.4}>
              <p className="mt-7 text-lg leading-[1.7] text-charcoal/80 md:text-xl">
                CareCraft partners with healthcare and community professionals to help families
                explore thoughtfully selected senior care options across Los Angeles.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-warmwhite pb-20">
        <div className="mx-auto grid max-w-site gap-10 px-4 sm:px-6 lg:grid-cols-12 lg:px-12">
          <Reveal className="lg:col-span-5">
            <div className="h-full rounded-2xl border border-inkline bg-ivory p-8">
              <h2 className="font-serif text-2xl font-medium text-forest">Who CareCraft works with</h2>
              <ul className="mt-5 flex flex-wrap gap-2.5">
                {PRO_CHIPS.map((c) => (
                  <li
                    key={c}
                    className="rounded-full border border-forest/20 bg-warmwhite px-4 py-1.5 text-[0.8rem] text-forest"
                  >
                    {c}
                  </li>
                ))}
              </ul>
              <h2 className="mt-10 font-serif text-2xl font-medium text-forest">Care categories</h2>
              <ul className="mt-5 space-y-2.5 text-[0.95rem] text-charcoal/80">
                {CARE_OPTIONS.map((o) => (
                  <li key={o.id} className="flex gap-3">
                    <span className="mt-[0.55em] h-1.5 w-1.5 shrink-0 rounded-full bg-clay" aria-hidden="true" />
                    {o.title}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="flex h-full flex-col gap-6">
              <div className="rounded-2xl border border-inkline p-8">
                <h2 className="font-serif text-2xl font-medium text-forest">Coverage &amp; response</h2>
                <p className="mt-4 leading-[1.7] text-charcoal/80">
                  CareCraft is locally based in Los Angeles and serves Los Angeles.
                </p>
                <p className="mt-3 leading-[1.7] text-charcoal/80">
                  CareCraft responds to calls and emails within 24 hours.
                </p>
                <ul className="mt-6 space-y-2.5">
                  {["Personally vetted options", "Local knowledge", "Response within 24 hours"].map((h) => (
                    <li key={h} className="flex items-center gap-3 text-[0.95rem] font-medium text-forest">
                      <span className="h-1.5 w-1.5 rounded-full bg-clay" aria-hidden="true" />
                      {h}
                    </li>
                  ))}
                </ul>
                <div className="mt-7 flex flex-wrap gap-x-10 gap-y-4">
                  <a
                    href={SITE.phoneHref}
                    data-testid="professionals-phone"
                    className="inline-flex items-center gap-2.5 font-medium text-forest transition-colors hover:text-clay"
                  >
                    <Phone className="h-5 w-5 text-clay" aria-hidden="true" /> {SITE.phoneDisplay}
                  </a>
                  <a
                    href={`mailto:${SITE.email}`}
                    data-testid="professionals-email"
                    className="inline-flex items-center gap-2.5 font-medium text-forest transition-colors hover:text-clay"
                  >
                    <Mail className="h-5 w-5 text-clay" aria-hidden="true" /> {SITE.email}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-xl border border-clay/40 bg-clay-soft p-5">
                <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-clay" aria-hidden="true" />
                <p className="text-[0.85rem] leading-relaxed text-charcoal/80">
                  Please do not submit sensitive medical information through the general referral
                  form. Share only a general care need and a non-sensitive identifier unless
                  CareCraft has approved a secure process for your organization.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-ivory py-20 md:py-24">
        <div className="mx-auto max-w-site px-4 sm:px-6 lg:px-12">
          <Reveal>
            <Eyebrow>How referrals work</Eyebrow>
            <h2 className="mt-6 max-w-2xl font-serif text-3xl font-medium tracking-tight text-forest md:text-4xl">
              Three steps, and the family is in good hands.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {PROCESS.map((p, i) => (
              <Reveal key={p.title} delay={0.07 * i}>
                <article className="h-full rounded-2xl border border-inkline bg-warmwhite p-8">
                  <p.icon className="h-7 w-7 text-clay" aria-hidden="true" />
                  <p className="mt-5 font-serif text-lg text-clay" aria-hidden="true">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <h3 className="mt-1 font-serif text-xl font-medium text-forest">{p.title}</h3>
                  <p className="mt-3 leading-[1.65] text-charcoal/80">{p.body}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-warmwhite py-20 md:py-28" id="refer">
        <div className="mx-auto max-w-site px-4 sm:px-6 lg:px-12">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <Reveal>
                <Eyebrow>Refer a family</Eyebrow>
                <h2 className="mt-6 font-serif text-3xl font-medium leading-tight tracking-tight text-forest md:text-4xl">
                  Send a referral
                </h2>
                <p className="mt-5 leading-[1.7] text-charcoal/80">
                  The form takes about two minutes. If it's urgent, call {SITE.phoneDisplay} directly.
                  A referral by phone is just as welcome.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-8">
              <Reveal delay={0.1}>
                <ProfessionalReferralForm />
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
